import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Mic, HeartPulse, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Brand Ambassador — IIY Phytoventra × Bella Glanville.
 *
 * Cinematic, deep-navy + champagne-gold pull-out section anchored on the
 * official ambassador portrait, a partnership narrative, a pull-quote, and a
 * three-credential grid (Academic / Voice / Advocate).
 *
 * Drop into Home.js where the homepage flow needs a "trusted endorsement"
 * beat — typically right after the credibility strip and before the science
 * section.
 */

const NAVY = '#001F3F';
const GOLD = '#C5B358';

const credentials = [
  {
    icon: GraduationCap,
    eyebrow: 'The Academic',
    title: "King's College London &amp; Harvard",
    body: 'A unique fusion of intellectual depth and creative artistry, grounded in two of the world&rsquo;s leading institutions.',
  },
  {
    icon: Mic,
    eyebrow: 'The Voice',
    title: 'TEDx Speaker',
    body: 'Exploring the intersection of psychology, technology and human potential on global stages.',
  },
  {
    icon: HeartPulse,
    eyebrow: 'The Advocate',
    title: 'Holistic Wellness',
    body: 'A dedicated voice for mitochondrial support and science-driven wellness that begins at the cellular level.',
  },
];

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], delay },
});

export const BrandAmbassadorSection = () => {
  return (
    <section
      className="relative overflow-hidden py-24 md:py-32"
      style={{ backgroundColor: NAVY }}
      data-testid="brand-ambassador-section"
    >
      {/* Atmospheric champagne glow + soft grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            `radial-gradient(60% 50% at 80% 20%, ${GOLD}1A 0%, transparent 60%),` +
            `radial-gradient(45% 45% at 15% 85%, ${GOLD}14 0%, transparent 70%)`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Section eyebrow */}
        <motion.div {...fadeIn(0)} className="mb-10 md:mb-16 text-center">
          <div
            className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-[0.32em] font-semibold mb-5"
            style={{
              color: GOLD,
              backgroundColor: `${GOLD}1A`,
              border: `1px solid ${GOLD}40`,
            }}
            data-testid="brand-ambassador-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GOLD }} />
            The New Face of Active Living
          </div>
          <h2
            className="font-heading text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
            data-testid="brand-ambassador-title"
          >
            Where <em className="not-italic" style={{ color: GOLD }}>Science</em> Meets <em className="not-italic" style={{ color: GOLD }}>Soul.</em>
          </h2>
          <p className="mt-4 text-white/60 text-sm md:text-base tracking-[0.2em] uppercase">
            IIY Phytoventra <span style={{ color: GOLD }}>×</span> Bella Glanville
          </p>
        </motion.div>

        {/* Hero portrait + narrative */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: portrait */}
          <motion.div {...fadeIn(0.1)} className="lg:col-span-5 relative">
            <div
              className="relative rounded-2xl overflow-hidden ring-1"
              style={{ boxShadow: `0 30px 80px -20px ${GOLD}33`, ringColor: `${GOLD}40` }}
            >
              <img
                src="/bella-glanville/bella-1.jpg"
                alt="Bella Glanville — IIY Phytoventra Brand Ambassador"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="brand-ambassador-portrait"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    `linear-gradient(180deg, transparent 50%, ${NAVY}99 100%)`,
                }}
              />
            </div>
            {/* Caption plate */}
            <div
              className="mt-5 inline-flex items-baseline gap-3 px-5 py-3 rounded-lg"
              style={{ backgroundColor: `${GOLD}10`, border: `1px solid ${GOLD}30` }}
            >
              <span
                className="font-heading text-xl"
                style={{ color: GOLD, fontFamily: '"Playfair Display", serif' }}
              >
                Bella Glanville
              </span>
              <span className="text-white/70 text-xs tracking-[0.2em] uppercase">
                Brand Ambassador
              </span>
            </div>
          </motion.div>

          {/* Right: narrative + pull-quote */}
          <motion.div {...fadeIn(0.2)} className="lg:col-span-7 text-white/85">
            <p className="text-base md:text-lg leading-relaxed mb-6">
              We are proud to introduce <span className="text-white font-semibold">Bella Glanville</span> as the official Brand Ambassador for IIY Phytoventra. As a multi-talented British actress, TEDx speaker, and health advocate, Bella embodies the very essence of our <span style={{ color: GOLD }} className="font-semibold">&ldquo;Invest In Yourself&rdquo;</span> philosophy.
            </p>
            <p className="text-base md:text-lg leading-relaxed mb-10">
              With an academic background from <span className="text-white">King&rsquo;s College London</span> and <span className="text-white">Harvard</span>, Bella brings a unique fusion of intellectual depth and creative artistry to our mission. She isn&rsquo;t just a representative; she is a dedicated advocate for holistic health who understands that true vitality starts at the cellular level.
            </p>

            {/* Pull-quote */}
            <motion.figure
              {...fadeIn(0.3)}
              className="relative pl-8 md:pl-10 py-2"
              data-testid="brand-ambassador-quote"
            >
              <span
                aria-hidden="true"
                className="absolute left-0 top-0 bottom-0 w-[2px]"
                style={{ background: `linear-gradient(180deg, ${GOLD}, ${GOLD}33)` }}
              />
              <span
                aria-hidden="true"
                className="absolute -left-2 -top-3 text-6xl leading-none font-heading select-none"
                style={{ color: `${GOLD}55`, fontFamily: '"Playfair Display", serif' }}
              >&ldquo;</span>
              <blockquote
                className="text-xl md:text-2xl leading-snug font-light text-white"
                style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif', fontStyle: 'italic' }}
              >
                True wellness is the bridge between cutting-edge science and our daily pursuit of authenticity. I am thrilled to partner with IIY Phytoventra to help the world invest in their most vital asset: themselves.
              </blockquote>
              <figcaption className="mt-4 text-sm tracking-[0.2em] uppercase" style={{ color: GOLD }}>
                — Bella Glanville
              </figcaption>
            </motion.figure>
          </motion.div>
        </div>

        {/* Credentials grid */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-3 gap-5 md:gap-6">
          {credentials.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.eyebrow}
                {...fadeIn(0.15 + i * 0.1)}
                className="relative p-7 rounded-2xl backdrop-blur-sm transition-all duration-500 hover:translate-y-[-4px]"
                style={{
                  backgroundColor: `${GOLD}08`,
                  border: `1px solid ${GOLD}25`,
                }}
                data-testid={`brand-ambassador-credential-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${GOLD}1A`, color: GOLD }}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <p
                  className="text-[10px] tracking-[0.28em] uppercase font-semibold mb-2"
                  style={{ color: GOLD }}
                >
                  {c.eyebrow}
                </p>
                <h3
                  className="text-white text-lg font-semibold mb-2"
                  dangerouslySetInnerHTML={{ __html: c.title }}
                />
                <p
                  className="text-white/65 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: c.body }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div {...fadeIn(0.4)} className="mt-16 md:mt-20 text-center">
          <Link
            to="/about"
            data-testid="brand-ambassador-cta"
            className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:gap-3.5"
            style={{
              backgroundColor: GOLD,
              color: NAVY,
              boxShadow: `0 12px 40px -12px ${GOLD}80`,
            }}
          >
            Explore the IIY Philosophy
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-4 text-white/50 text-xs tracking-[0.2em] uppercase">
            Join Bella and the IIY community in the pursuit of science-backed vitality.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default BrandAmbassadorSection;
